import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Decode HTML entities in a string (e.g., &#39; -> ', &amp; -> &, &quot; -> ")
 */
export function decodeHtmlEntities(str: string | null | undefined): string {
  if (!str) return '';

  // Common HTML entities
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&#x27;': "'",
    '&apos;': "'",
    '&#96;': '`',
    '&nbsp;': ' ',
  };

  // Replace named/numeric entities
  let decoded = str.replace(/&(?:#x?[\da-fA-F]+|[a-zA-Z]+);/g, (match) => {
    // Check known entities first
    if (entities[match]) return entities[match];

    // Handle numeric entities (&#123; or &#x7B;)
    if (match.startsWith('&#x')) {
      const code = parseInt(match.slice(3, -1), 16);
      return String.fromCharCode(code);
    } else if (match.startsWith('&#')) {
      const code = parseInt(match.slice(2, -1), 10);
      return String.fromCharCode(code);
    }

    return match; // Return as-is if unknown
  });

  return decoded;
}

/**
 * Sanitize a string for use as a filename or HTTP header value.
 * Normalizes smart quotes to regular quotes and removes problematic characters.
 */
export function sanitizeForFilename(str: string | null | undefined): string {
  if (!str) return '';

  return str
    // Normalize smart/curly quotes to regular quotes
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")  // Single quotes: ' ' ‚ ‛
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')  // Double quotes: " " „ ‟
    // Normalize dashes
    .replace(/[\u2013\u2014]/g, '-')              // En-dash, Em-dash: – —
    // Normalize ellipsis
    .replace(/\u2026/g, '...')                     // Ellipsis: …
    // Remove other non-ASCII characters that could cause header issues
    .replace(/[^\x00-\x7F]/g, '')
    .trim();
}

export function formatBytes(bytes: number | bigint | string | null | undefined, decimals = 2) {
  if (bytes === null || bytes === undefined) return '0 Bytes';

  const bytesNum = typeof bytes === 'string' ? parseInt(bytes, 10) : Number(bytes);
  if (isNaN(bytesNum) || bytesNum === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytesNum) / Math.log(k));

  return parseFloat((bytesNum / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
